import Icon from "../NGMaterialIcon/NGMaterialIcon";
import {
  Autocomplete,
  Checkbox,
  Chip,
  InputAdornment,
  NativeSelect,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Box,
  Stack,
} from "@mui/material";
import { batch, useComputed, useSignal, useSignalEffect } from "@preact/signals-react";
import { debounce, isArray, isNil, isObject, isString } from "lodash-es";
import { isNative } from "../../library/native";
import { INGMultiSelectProps } from "../../library/NGFieldExtensions";
import { publishUserAction } from "../../library/analytics";
import { getScope, getState, setupHandlers, setupLocalState, isEmbeded } from "../../library/dataService";
import { getExprValue } from "../../library/interpreter";
import { log } from "../../library/logger";
import {
  getClassName,
  getCustomLabel,
  getFirstNonEmpty,
  getTestId,
  getsxObject,
  isNullOrEmpty,
  partitionMUIClasses,
  translateText,
} from "../../library/utils";
import NGIcon from "../NGIcon/NGIcon";
import { setAdornment } from "../../library/adornmentUtil";
import { FocusEvent } from "react";

const AnimatedLoadingText = ({ text = "Loading", interval = 300 }) => {
  const dots = useSignal("");

  useSignalEffect(() => {
    const timer = setInterval(() => {
      dots.value = dots.value.length >= 3 ? "" : dots.value + ".";
    }, interval);

    return () => clearInterval(timer);
  });

  return (
    <Typography component="span">
      {text}
      {dots.value}
    </Typography>
  );
};

export default function NGMultiSelect({ config, context }: INGMultiSelectProps) {
  config.DefaultValue = config.DefaultValue ?? ((config.Multiple ? [] : null) as any);

  const tag = "NGMultiSelect";

  const local = setupLocalState(
    config,
    {
      Visible: useSignal(config.Visible ?? true),
      Disabled: useSignal(config.Disabled ?? false),
      Multiple: useSignal(config.Multiple ?? false),
      FreeSolo: useSignal(config.FreeSolo ?? false),
      DefaultValue: useSignal(config.DefaultValue),
      Value: useSignal(config.DefaultValue),
      UseGroupBy: useSignal(config.UseGroupBy ?? false),
      MultiSelectPossibleValues: useSignal(config.MultiSelectPossibleValues ?? []),
      Placeholder: useSignal(config.Placeholder ?? ""),
      SearchFilter: useSignal(config.SearchFilter ?? ""),
      Classes: useSignal(config.Classes ?? []),
      Style: useSignal(config.Style ?? {}),
      NoOptionsText: useSignal(config.NoOptionsText ?? "No Options"),
      ClearAfterSelect: useSignal(config.ClearAfterSelect ?? false),
      GroupBy: useSignal(config.GroupBy ?? ""),
      GroupClasses: useSignal(config.GroupClasses ?? []),
      LabelClasses: useSignal(config.LabelClasses ?? []),
      CaptionClasses: useSignal(config.CaptionClasses ?? []),
      CaptionTemplate: useSignal(config.CaptionTemplate ?? ""),
      Loading: useSignal(config.Loading ?? false),
      LoadingText: useSignal(config.LoadingText ?? "Loading"),
      ShowSelectAll: useSignal(config.ShowSelectAll ?? false),
      AutoselectFirstOption: useSignal(config.AutoselectFirstOption ?? false),
    },
    context
  );
  const handlers = setupHandlers(config, context);
  const showNativeSelect = isNative() && !isSAYT() && !local.FreeSolo.value && !local.Multiple.value;

  const isAllSelected = useComputed(() => {
    if (!local.ShowSelectAll.value || !isArray(selected.value)) return false;
    return selected.value.length === local.MultiSelectPossibleValues.value.length;
  });

  // Create select all option object
  const selectAllOption = useComputed(() => {
    if (!local.ShowSelectAll.value) return null;
    return {
      __isSelectAll: true,
      [config.ValueExpression || "value"]: "__SELECT_ALL__",
      [config.LabelExpression || config.OptionsLabelExpression || "label"]: isAllSelected.value
        ? translateText("Unselect All", context)
        : translateText("Select All", context),
    };
  });

  useSignalEffect(() => {
    if (!local.AutoselectFirstOption.value) return;

    if (isNil(local.Value.value) && local.MultiSelectPossibleValues.value?.length > 0) {
      //local.Value.value = local.MultiSelectPossibleValues.value[0];
      onChangeDo(null, local.MultiSelectPossibleValues.value[0], true);
    }
  });

  const selected = useComputed(() => {
    log.debug(tag, "ENTER useComputed with local.Value.value:", local.Value.value);

    if (!isArray(local.MultiSelectPossibleValues.value)) {
      log.error(tag, "MultiSelectPossibleValues is not an array", local.MultiSelectPossibleValues.value);
      return [];
    }

    const vex = config.ValueExpression;
    const lex = config.LabelExpression;
    const v = local.Multiple.value && isString(local.Value.value) ? local.Value.value.split(",") : local.Value.value;
    const values = local.MultiSelectPossibleValues.value;

    let r = null;
    if (isNil(v)) {
      r = null;
    } else {
      if (vex) {
        if (local.Multiple.value) {
          // If multiple values are expected, process v as an array.
          if (config.FreeSolo) {
            r = v.map((x) => {
              if (lex) return isObject(x) ? x[lex] : x;

              return isObject(x) ? x[vex] : x;
            });
          } else {
            r = values.filter((x) => v.includes(isObject(x) ? x[vex] : x));
          }
        } else {
          if (config.FreeSolo && !values.length) r = v;
          else r = values.find((x) => (isObject(x) ? x[vex] : x) == v);
        }
      } else {
        r = v;
      }
    }
    log.debug(tag, "EXIT useComputed", r, v, local.Value.value);
    return r ?? (local.Multiple.value ? [] : config.FreeSolo ? "" : null);
  });

  const inputProps: any = {};

  setAdornment({
    position: "start",
    adornment: config.StartAdornment,
    inputProps,
    context,
    configId: config.Id,
  });
  setAdornment({
    position: "end",
    adornment: config.EndAdornment,
    inputProps,
    context,
    configId: config.Id,
  });

  // Retain legacy Adornment logic for backward compatibility
  if (!isNil(config.Adornment)) {
    const p = config.Adornment?.Position?.toLowerCase() ?? "start";
    inputProps[`${p}Adornment`] = (
      <InputAdornment position={p as "start" | "end"}>
        <NGIcon config={{ Id: config.Id + "_LegacyIcon", IconName: "search" }} context={context} />
      </InputAdornment>
    );
  }

  const handleInputChange = debounce((event, newInputValue) => {
    // if (isNullOrEmpty(newInputValue)) {
    //   if (local.MultiSelectPossibleValues.value.length > 0) {
    //     local.MultiSelectPossibleValues.value =
    //       config.MultiSelectPossibleValues || [];
    //     console.log(
    //       "Resetting MultiSelectPossibleValues",
    //       local.MultiSelectPossibleValues.value
    //     );
    //   }
    // } else
    local.SearchFilter.value = newInputValue;
  }, 300);

  function isSAYT() {
    if (!isNil(config.Bindings) && !isNullOrEmpty(config.Bindings.SearchFilter)) return true;

    return false;
  }

  function isComplexValueType() {
    log.debug(tag, "isComplexValueType", config.ValueType, config.ValueExpression);

    // If the ValueType is not set, but the ValueExpression is set, then it is a complex type
    if (isNil(config.ValueType)) return !isNil(config.ValueExpression);

    return config.ValueType?.toLowerCase() === "object" || config.ValueType?.toLowerCase() === "array";
  }

  //As per MUI documentation, when in SAYT mode, you need to disable the built-in filtering of the Autocomplete component by overriding the filterOptions prop
  const filterOptions = isSAYT() ? (x) => x : undefined;

  const getOptionLabel = (option: any): string => {
    //log.debug(tag, "enter getOptionLabel", option);
    if (isNil(option)) return "";

    const lex = config.OptionsLabelExpression ?? config.LabelExpression;

    const l: string = lex && isObject(option) ? (isNil(option[lex]) ? "" : option[lex]) : option;

    //log.debug(tag, "EXIT getOptionLabel", l, isObject(option));
    return l;
  };

  const getOptionValue = (option: any): string => {
    //log.debug(tag, "enter getOptionValue", option);
    if (isNil(option)) return "";
    const vex = config.ValueExpression || config.LabelExpression;

    const v: string = vex && isObject(option) ? (isNil(option[vex]) ? "" : option[vex]) : option;

    //log.debug(tag, "EXIT getOptionValue", v, isObject(option));
    return v;
  };

  const orderByGroup = (options?: any[], groupBy?: string) => {
    if (!groupBy || !isArray(options)) return options;

    return options.sort((a, b) => {
      const aGroup = a[groupBy];
      const bGroup = b[groupBy];
      if (aGroup === bGroup) return 0;
      return aGroup < bGroup ? -1 : 1;
    });
  };

  const groupBy = local.UseGroupBy.value
    ? (option) => {
      if (config.Bindings?.GroupBy) {
        const { state, form, parentState } = getState(context);
        const scope = getScope(context, config, state, form, option, parentState);
        return getExprValue(config.Bindings?.GroupBy, scope, null);
      } else if (config.GroupBy) {
        return option[config.GroupBy];
      }
    }
    : undefined;

  const sortedOptions = orderByGroup(local.MultiSelectPossibleValues.value, local.GroupBy.value);

  const getOptions = () => {
    let options = [];
    if (isArray(local.MultiSelectPossibleValues.value)) {
      options = local.UseGroupBy.value ? sortedOptions : local.MultiSelectPossibleValues.value;
    }

    // Add select all option at the beginning if enabled
    if (local.ShowSelectAll.value && selectAllOption.value) {
      return [selectAllOption.value, ...options];
    }

    return options;
  };

  function renderTemplate(str: string, optionObj: Record<string, unknown>) {
    if (!str) return "";
    let hasValue = false;
    const result = str.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, p1) => {
      const val = optionObj[p1];
      if (val !== undefined && val !== null) {
        hasValue = true;
        return String(val);
      }
      return "";
    });
    return hasValue ? result : "";
  }

  return (
    local.Visible.value && (
      <>
        {!showNativeSelect ? (
          <Autocomplete
            popupIcon={<Icon name="arrow_drop_down" />}
            data-type={config.__typename}
            disabled={local.Disabled.value}
            data-testid={getTestId(config)}
            sx={getsxObject(local.Style.value)}
            className={getClassName(local.Classes)}
            loading={local.Loading.value}
            loadingText={<AnimatedLoadingText text={local.LoadingText.value} />}
            multiple={config.Multiple as boolean}
            freeSolo={config.FreeSolo as boolean}
            disableClearable={config.DisableClearable as boolean}
            autoComplete={config.AutoComplete as boolean}
            autoHighlight={config.AutoHighlight as boolean}
            selectOnFocus={config.SelectOnFocus as boolean}
            autoSelect={config.AutoSelect as boolean}
            blurOnSelect={config.BlurOnSelect as any}
            disablePortal={isEmbeded()}
            options={getOptions()}
            groupBy={groupBy}
            filterOptions={filterOptions as any}
            noOptionsText={local.NoOptionsText.value}
            openOnFocus
            renderGroup={
              local.GroupClasses.value?.length || local.LabelClasses.value?.length || local.CaptionClasses.value?.length
                ? (params) => {
                  const { key, ...groupProps } = params as any;
                  const groupLabel = params.group;
                  const groupClasses = ["ngms-group-base", ...partitionMUIClasses(local.GroupClasses.value)];
                  return (
                    <li key={key} {...groupProps} className={groupClasses.join(" ")}>
                      <span className={[...partitionMUIClasses(local.LabelClasses.value)].join(" ")}>
                        {groupLabel}
                      </span>
                      <span className={[...partitionMUIClasses(local.CaptionClasses.value)].join(" ")}>
                        {params.children}
                      </span>
                    </li>
                  );
                }
                : undefined
            }
            renderInput={(params) => {
              delete (params as any).key;

              // Handle StartAdornment properly with chips in FreeSolo multiple mode
              const mergedInputProps = { ...params.InputProps };

              if (inputProps.startAdornment) {
                if (config.FreeSolo && config.Multiple && params.InputProps?.startAdornment) {
                  // Combine both the chips and the start adornment
                  mergedInputProps.startAdornment = (
                    <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                      {inputProps.startAdornment}
                      {params.InputProps.startAdornment}
                    </Stack>
                  );
                } else {
                  mergedInputProps.startAdornment = inputProps.startAdornment;
                }
              }

              if (inputProps.endAdornment) {
                mergedInputProps.endAdornment = inputProps.endAdornment;
              }

              return (
                <TextField
                  key={params.id}
                  {...params}
                  label={getCustomLabel(config.Label)}
                  placeholder={local.Placeholder.value}
                  slotProps={{
                    input: mergedInputProps,
                  }}
                />
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const p = getTagProps({ index });
                delete (p as any).key;

                return (
                  <Chip
                    key={index} // Pass the key prop directly
                    {...(p as any)} // Spread the other props
                    label={getOptionLabel(option)}
                  />
                );
              })
            }
            isOptionEqualToValue={(option, value) => {
              //log.debug(tag, "enter isOptionEqualToValue", option, value);

              // Handle select all option
              if (option?.__isSelectAll || value?.__isSelectAll) {
                return false; // Select all should never be considered equal to actual values
              }

              const vex = config.ValueExpression;

              if (value == null) return false;

              const l = vex ? option[vex] === (isObject(value) ? value[vex] : value) : option === value;

              //log.debug(tag, "EXIT isOptionEqualToValue", l);
              return l;
            }}
            getOptionLabel={getOptionLabel}
            value={selected.value}
            // {...handlers}
            onInputChange={(event, newInputValue) => {
              handleInputChange(event, newInputValue);
            }}
            onChange={(e, v) => {
              onChangeDo(e, v);
            }}
            onBlur={(e: FocusEvent<HTMLDivElement, Element>) => {
              log.debug(tag, "enter onBlur", e, local.Value.value);

              const inputValue = (e.target as any).value;
              if (
                (config.Multiple || config.FreeSolo) &&
                !isNullOrEmpty(inputValue) &&
                typeof window !== "undefined" &&
                window.KeyboardEvent
              ) {
                try {
                  const event = new KeyboardEvent("keydown", {
                    key: "Enter",
                    code: "Enter",
                    keyCode: 13,
                    charCode: 13,
                    bubbles: true,
                  });

                  e.target.dispatchEvent(event);
                } catch (error) {
                  log.warn(tag, "Dispatch keyboard event no available", error);
                }
              }

              if (!isNil((handlers as any).onBlur)) {
                (handlers as any).onBlur(e);
              }
            }}
            disableCloseOnSelect={config.CheckboxMultiSelect as boolean}
            renderOption={
              config.CheckboxMultiSelect || local.CaptionTemplate.value || local.ShowSelectAll.value
                ? (props, option, { selected: isSelected }) => {
                  const { key, ...optionProps } = props as any;
                  const lex = config.LabelExpression;
                  const template = local.CaptionTemplate.value;
                  const isSelectAllOption = option?.__isSelectAll;

                  const optionClass = [
                    "ngms-option-base",
                    isSelected ? "selected" : "",
                    isSelectAllOption ? "select-all-option" : "",
                    ...partitionMUIClasses(local.LabelClasses.value),
                  ].join(" ");

                  const renderedTemplate = !isSelectAllOption && template ? renderTemplate(template, option) : "";

                  return (
                    <li
                      key={key}
                      {...optionProps}
                      className={
                        template && !isSelectAllOption
                          ? optionClass
                          : `${optionProps.className} ${isSelectAllOption ? "select-all-option" : ""}`
                      }
                    >
                      {config.CheckboxMultiSelect && (
                        <Checkbox
                          icon={<Icon name="check_box_outline_blank" fontSize="small" />}
                          checkedIcon={<Icon name="check_box" fontSize="small" />}
                          style={{ marginRight: 8 }}
                          checked={isSelectAllOption ? isAllSelected.value : isSelected}
                          indeterminate={
                            isSelectAllOption &&
                            isArray(selected.value) &&
                            selected.value.length > 0 &&
                            selected.value.length < local.MultiSelectPossibleValues.value.length
                          }
                        />
                      )}
                      {isSelectAllOption ? (
                        <span>{getOptionLabel(option)}</span>
                      ) : template && renderedTemplate ? (
                        <>
                          <p
                            className={[
                              "ngms-option-base-label",
                              ...partitionMUIClasses(local.CaptionClasses.value),
                            ].join(" ")}
                          >
                            {lex ? option[lex] : getOptionLabel(option)}
                          </p>
                          <span
                            className={["ngms-caption-base", ...partitionMUIClasses(local.CaptionClasses.value)].join(
                              " "
                            )}
                          >
                            {renderedTemplate}
                          </span>
                        </>
                      ) : lex ? (
                        option[lex]
                      ) : (
                        option
                      )}
                    </li>
                  );
                }
                : undefined
            }
          />
        ) : (
          <FormControl fullWidth>
            <NativeSelect
              data-type={config.__typename}
              disabled={local.Disabled.value}
              data-testid={getTestId(config)}
              value={getOptionValue(selected.value)}
              sx={getsxObject(local.Style.value, { marginTop: "0px!important" })}
              className={getClassName(local.Classes)}
              onChange={(e) => {
                const v = e.target.value;
                onChangeDo(e, v);
              }}
              onBlur={(e) => {
                if (!isNil((handlers as any).onBlur)) {
                  (handlers as any).onBlur(e);
                }
              }}
            >
              <option value={undefined}>{local.Placeholder.value || ""}</option>
              {local.MultiSelectPossibleValues.value.map((option, index) => (
                <option key={index} value={isObject(option) ? getOptionValue(option) : option}>
                  {getOptionLabel(option)}
                </option>
              ))}
            </NativeSelect>
            {getCustomLabel(config.Label) && local.MultiSelectPossibleValues?.value?.length > 0 && (
              <InputLabel>{getCustomLabel(config.Label)}</InputLabel>
            )}
          </FormControl>
        )}
      </>
    )
  );

  function onChangeDo(e, v: any, stopPropagation = false) {
    log.debug(tag, "enter onChange", v, isComplexValueType());
    try {
      // Handle select all functionality
      if (local.ShowSelectAll.value && config.Multiple && isArray(v)) {
        const selectAllItem = v.find(item => item?.__isSelectAll);
        if (selectAllItem) {
          // Select all was clicked
          if (isAllSelected.value) {
            // If all are selected, unselect all
            v = [];
          } else {
            // If not all are selected, select all regular options (exclude select all option)
            v = local.MultiSelectPossibleValues.value.slice();
          }
        }
      }
      // try set local.Value.value
      if (v == null) {
        local.Value.value = v;
      } else {
        // If v is not null or undefined, check the conditions to determine the assignment.
        if (config.ValueExpression) {
          // If config has a truthy ValueExpression, further evaluation is required.
          if (config.Multiple) {
            // If expecting multiple values, process v as an array.
            local.Value.value = (v as any).map((x) =>
              // For each element in the array, if it's an object, extract the value using ValueExpression as the key.
              // Otherwise, return the element as is.
              isObject(x) ? x[config.ValueExpression as string] : x
            );
          } else {
            // If not expecting multiple values, check if v is an object to extract a single value using ValueExpression.
            local.Value.value = isObject(v) ? v[config.ValueExpression as string] : v;
          }
        } else if (config.LabelExpression) {
          // if no ValueExpression but LabelExpression is present, assign the value based on LabelExpression.
          // Value should be an object
          local.Value.value = isObject(v)
            ? v
            : local.MultiSelectPossibleValues.value.find((x) => x[config.LabelExpression as string] === v) ?? v;
        } else {
          // If there's no ValueExpression in the config, assign v directly.
          local.Value.value = v;
        }
      }
    } catch (error) {
      log.warn(tag, "Error assigning value", error);
    }

    if (config.FreeSolo) {
      let isNewValue = true;
      for (let i = 0; i < local.MultiSelectPossibleValues.value.length; i++) {
        if (local.MultiSelectPossibleValues.value[i] === v) {
          isNewValue = false;
        }
      }

      if (isNewValue) {
        log.debug(tag, "A new value has been create in Freesolo mode", v, local.MultiSelectPossibleValues.value);

        if (!stopPropagation && !isNil((handlers as any).onNewValue)) {
          (handlers as any).onNewValue(e, v);
        }
      }
    }

    log.debug(tag, "EXIT onChange", v, local.Value.value);
    const [_, classes] = partitionMUIClasses(local.Classes.value);
    const value = config.LabelExpression && v ? v[config.LabelExpression] : v;
    const valueString = isObject(value) ? JSON.stringify(value) : String(value);
    const isImplicit = local.AutoselectFirstOption.value && isNil(e);
    const action = publishUserAction(config, context, "multi_select_change", e, {
      Value: valueString,
      EventCategory: "engagement",
      ElementText: getFirstNonEmpty(config.Label, config.Placeholder, "MultiSelect"),
      ElementClass: classes.join(" "),
      IsImplicit: isImplicit,
    });

    let handlerValue = v;
    if (showNativeSelect && (config.ValueExpression || config.LabelExpression)) {
      handlerValue = isObject(v)
        ? v
        : local.MultiSelectPossibleValues.value.find((x) => {
          const vex = config.ValueExpression;
          const lex = config.LabelExpression;
          return (vex && x[vex] === v) || (lex && x[lex] === v) || (!vex && !lex && x === v);
        });
    }

    if (!stopPropagation && !isNil((handlers as any).onChange)) {
      (handlers as any).onChange(e, handlerValue, action, () => {
        if (local.ClearAfterSelect.value) {
          log.debug(tag, "Clearing the value after select.  Interval:", config.ClearInterval);
          setTimeout(
            () =>
              batch(() => {
                local.Value.value = null;
                local.SearchFilter.value = "";
              }),
            config.ClearInterval ?? 500
          );
        }
      });
    }
  }
}
